/**
 * MergeReview — Phase 1.14.3 merge-listings review + commit page.
 *
 * Route: /t/:tenantSlug/merges/:eventId
 *
 * Renders a draft merge_event for the operator to review + commit (or
 * cancel). The draft was created via:
 *   - inline "Mark as duplicate of…" flow on OrganizationDetail, OR
 *   - direct URL navigation from the AdminReview Merges sub-tab
 *
 * The page is the SAME route shape for both entry points — once the
 * draft exists, this page is the single review surface. Already-
 * committed and already-cancelled drafts render in read-only mode
 * (forensic-only view; useful for audit walkbacks).
 *
 * Phase 1.14.3.1 polish — brand-aligned (sage / cream / warm palette,
 * Playfair Display headlines, Inter body) + reparent-counts blast-
 * radius panel + softer microcopy. The clinical-warm-direct voice
 * (per applying-brand-guidelines): "Keep" / "Replace with" instead of
 * "winner" / "loser" in column headers; "Apply merge" instead of
 * "Commit merge"; "Discard draft" instead of "Cancel draft"; "(empty)"
 * instead of em-dash as the null indicator.
 *
 * A failed apply shows one sentence in a banner above the choices
 * (src/lib/mergeRefusals.ts). On 409 conflict_stale_draft the page also
 * refetches the merge_event so the operator re-confirms current values.
 *
 * Capability gate: useCapability('merge_providers') gates the entire
 * page — owner + data_steward only. Non-grantees see a 403-shaped
 * empty state.
 */

import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Merge } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canMergeProviders } from '@/lib/capabilities';
import { api } from '@/lib/api';
import { MergeFieldPicker } from '@/components/MergeFieldPicker';
import { PageShell } from '@/components/primitives/PageShell';
import { Skeleton } from '@/components/Skeleton';
import { ErrorBanner } from '@/components/ErrorBanner';
import { contactSentence, errorCodeOf } from '@/lib/errorCopy';
import { mergeRefusalText } from '@/lib/mergeRefusals';
import { useSupportContact } from '@/hooks/useSupportContact';
import type { MergeFieldDecisions } from '@/lib/types';

export function MergeReview(): ReactNode {
  const { tenantSlug, eventId } = useParams<{
    tenantSlug: string;
    eventId: string;
  }>();
  const { whoami } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const canMerge = canMergeProviders(whoami);

  const eventQuery = useQuery({
    queryKey: ['merge-event', tenantSlug, eventId],
    queryFn: () => api.getProviderMerge(tenantSlug ?? '', eventId ?? ''),
    enabled: Boolean(tenantSlug && eventId && canMerge),
  });

  const event = eventQuery.data;
  const fieldDecisions: MergeFieldDecisions | undefined = event?.field_decisions;

  const [localDecisions, setLocalDecisions] = useState<MergeFieldDecisions | null>(
    null,
  );

  useEffect(() => {
    if (fieldDecisions && localDecisions === null) {
      setLocalDecisions(fieldDecisions);
    }
  }, [fieldDecisions, localDecisions]);

  const fieldsList = useMemo(() => {
    if (!fieldDecisions) return [];
    return Object.entries(fieldDecisions.main_row).map(([field_name, d]) => ({
      field_name,
      winner_value: d.winner_value,
      loser_value: d.loser_value,
      status: deriveStatus(d.winner_value, d.loser_value),
    }));
  }, [fieldDecisions]);

  const updateMutation = useMutation({
    mutationFn: async (next: MergeFieldDecisions) =>
      api.updateProviderMergeDraft(tenantSlug ?? '', eventId ?? '', next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['merge-event', tenantSlug, eventId],
      });
    },
  });

  const [applyError, setApplyError] = useState<string | null>(null);
  const contact = useSupportContact();

  const commitMutation = useMutation({
    mutationFn: async () =>
      api.commitProviderMerge(tenantSlug ?? '', eventId ?? ''),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: ['merge-event', tenantSlug, eventId],
      });
      navigate(
        `/t/${tenantSlug}/organizations/${encodeURIComponent(result.winner_id)}`,
      );
    },
    onError: (err: unknown) => {
      // A stale draft reloads the event so the choices below are current.
      if (errorCodeOf(err) === 'conflict_stale_draft') {
        void queryClient.invalidateQueries({
          queryKey: ['merge-event', tenantSlug, eventId],
        });
      }
      setApplyError(mergeRefusalText(err, contact));
    },
  });

  const [cancelReason, setCancelReason] = useState('');
  // The reason is read at the press and travels as the variables; an
  // empty one is still refused inside the mutation.
  const cancelMutation = useMutation({
    mutationFn: async (reason: string) => {
      if (reason.length === 0) {
        throw new Error('reason_required');
      }
      return api.cancelProviderMergeDraft(tenantSlug ?? '', eventId ?? '', reason);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['merge-event', tenantSlug, eventId],
      });
    },
  });

  if (!canMerge) {
    return (
      <PageShell
        title="Merge review"
        maxWidth="wide"
        access={{
          allowed: false,
          deniedContent: (
            <p className="mt-4 text-sm text-ink-soft">
              {contactSentence(
                "Merging listings isn't part of your access. If two listings are the same place, tell {contact_admin}.",
                contact,
              )}
            </p>
          ),
        }}
      >
        <div />
      </PageShell>
    );
  }

  if (eventQuery.isPending) {
    return (
      <PageShell title="Merge review" maxWidth="wide">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-4 h-64 w-full" />
      </PageShell>
    );
  }

  if (eventQuery.isError || !event) {
    return (
      <PageShell title="Merge review" maxWidth="wide">
        <ErrorBanner error={eventQuery.error} what="this merge draft" />
      </PageShell>
    );
  }

  const isDraft = event.status === 'draft';
  const decisionsForRender = localDecisions ?? fieldDecisions;

  if (!decisionsForRender) {
    return (
      <PageShell title="Merge review" maxWidth="wide">
        <ErrorBanner error={null} what="this merge draft" />
      </PageShell>
    );
  }

  // Sort fields so that conflicts surface first, then auto-inherit,
  // then agreeing rows last (compact). Operators see what needs
  // attention without scrolling past dozens of ✓-agree rows.
  const sortedFields = [...fieldsList].sort((a, b) => {
    const order = { conflict: 0, auto_inherit_non_null: 1, agree: 2 };
    return order[a.status] - order[b.status];
  });

  const conflictCount = fieldsList.filter((f) => f.status === 'conflict').length;
  const inheritCount = fieldsList.filter(
    (f) => f.status === 'auto_inherit_non_null',
  ).length;

  return (
    <PageShell maxWidth="wide">
      {/* Breadcrumb */}
      <div className="mb-2 text-xs uppercase tracking-wider text-sage-500">
        Merge review
      </div>

      {/* Heading — Playfair Display per brand */}
      <h1 className="font-display text-3xl text-sage-800 leading-tight">
        Merge{' '}
        <span className="text-ink-soft">{event.loser_name}</span>{' '}
        <span className="text-sage-400">into</span>{' '}
        <span className="text-sage-800">{event.winner_name}</span>
      </h1>

      {/* Status pill + timestamps */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <StatusPill status={event.status} />
        {event.status === 'committed' && event.committed_at && (
          <span className="text-ink-soft">
            applied {new Date(event.committed_at).toLocaleString()}
          </span>
        )}
        {event.status === 'cancelled' && event.cancelled_at && (
          <span className="text-ink-soft">
            discarded {new Date(event.cancelled_at).toLocaleString()}
          </span>
        )}
        {event.status === 'draft' && (
          <span className="text-ink-soft">
            started {new Date(event.proposed_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* Blast-radius panel — Phase 1.14.3.1 polish surfaces brief §6.2 */}
      {isDraft && event.reparent_counts && (
        <BlastRadius
          counts={event.reparent_counts}
          loserName={event.loser_name}
        />
      )}

      {/* Decision overview */}
      {isDraft && (
        <p className="mt-5 text-sm text-ink-soft">
          {conflictCount === 0 && inheritCount === 0 ? (
            <>
              These two records agree on every field. You can apply the merge
              with one click below.
            </>
          ) : (
            <>
              {conflictCount > 0 && (
                <>
                  <span className="font-semibold text-sage-800">
                    {conflictCount}
                  </span>{' '}
                  field{conflictCount === 1 ? '' : 's'} need
                  {conflictCount === 1 ? 's' : ''} a choice;
                </>
              )}
              {conflictCount > 0 && inheritCount > 0 && ' '}
              {inheritCount > 0 && (
                <>
                  <span className="font-semibold text-sage-800">
                    {inheritCount}
                  </span>{' '}
                  will inherit the non-empty side.
                </>
              )}
            </>
          )}
        </p>
      )}

      {/* A failed apply: a stale draft, another refusal or a fault */}
      {applyError && (
        <div
          data-testid="merge-review-error"
          role="alert"
          className="mt-5 rounded-lg border border-warm-300 bg-warm-50 px-4 py-3 text-sm text-warm-700"
        >
          {applyError}
        </div>
      )}

      {/* Field comparison card */}
      <div className="mt-6 overflow-hidden rounded-lg border border-sage-200 bg-paper shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-sage-200 bg-sage-50 text-xs uppercase tracking-wider text-sage-700">
              <th className="px-4 py-3 font-semibold">Field</th>
              <th className="px-4 py-3 font-semibold">Keep</th>
              <th className="px-4 py-3 font-semibold">Replace with</th>
              <th className="px-4 py-3 font-semibold">Or enter your own</th>
            </tr>
          </thead>
          <tbody>
            {sortedFields.map((field) => {
              const decision = decisionsForRender.main_row[field.field_name];
              if (!decision) return null;
              return (
                <MergeFieldPicker
                  key={field.field_name}
                  field={field}
                  decision={decision}
                  disabled={!isDraft}
                  onChange={(next) => {
                    const updated: MergeFieldDecisions = {
                      main_row: {
                        ...decisionsForRender.main_row,
                        [field.field_name]: next,
                      },
                      junction_decisions: decisionsForRender.junction_decisions,
                    };
                    setLocalDecisions(updated);
                    updateMutation.mutate(updated);
                  }}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bottom action bar */}
      {isDraft ? (
        <div className="mt-8 rounded-lg border border-sage-200 bg-paper p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex-1">
              <label
                htmlFor="cancel-reason"
                className="block text-xs font-semibold uppercase tracking-wider text-sage-700"
              >
                Want to discard this draft instead?
              </label>
              <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  id="cancel-reason"
                  type="text"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Brief note on why (required)"
                  className="form-input flex-1"
                />
                <button
                  type="button"
                  onClick={() => cancelMutation.mutate(cancelReason.trim())}
                  disabled={
                    cancelMutation.isPending || cancelReason.trim().length === 0
                  }
                  className="min-h-11 whitespace-nowrap rounded-lg border border-sage-300 bg-paper px-4 py-2 text-sm font-semibold text-sage-700 hover:bg-sage-50 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                >
                  Discard draft
                </button>
              </div>
            </div>
            <div className="sm:pl-6 sm:border-l sm:border-sage-200">
              <button
                type="button"
                onClick={() => commitMutation.mutate()}
                disabled={commitMutation.isPending}
                className="min-h-11 rounded-lg bg-sage-800 px-6 py-2.5 text-sm font-semibold text-cream shadow-sm hover:bg-sage-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                {commitMutation.isPending ? 'Applying…' : 'Apply merge'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-lg border border-sage-200 bg-sage-50 px-4 py-3 text-sm text-sage-700">
          This merge has reached its final state and can&apos;t be changed.
        </div>
      )}
    </PageShell>
  );
}

function StatusPill({ status }: { status: 'draft' | 'committed' | 'cancelled' }): ReactNode {
  const styles = {
    draft: 'bg-warm-100 text-warm-700 border-warm-200',
    committed: 'bg-sage-100 text-sage-800 border-sage-200',
    cancelled: 'bg-sand text-ink-soft border-sage-800/[8%]',
  } as const;
  const labels = {
    draft: 'In progress',
    committed: 'Applied',
    cancelled: 'Discarded',
  } as const;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

function BlastRadius({
  counts,
  loserName,
}: {
  counts: NonNullable<import('@/lib/types').MergeEventSummary['reparent_counts']>;
  loserName: string;
}): ReactNode {
  // Build a compact human-readable list of non-zero reparent items.
  const items: Array<{ label: string; n: number }> = [
    { label: 'note', n: counts.provider_notes },
    { label: 'affiliated contact', n: counts.provider_individual_organizations },
    { label: 'service area', n: counts.provider_service_areas },
    { label: 'specialty', n: counts.provider_specializations },
    { label: 'subcategory', n: counts.provider_subcategorizations },
    { label: 'highlight', n: counts.provider_highlight_assignments },
    { label: 'delivery method', n: counts.provider_service_model_assignments },
    { label: 'document', n: counts.work_item_attachments ?? 0 },
    { label: 'pending suggestion', n: counts.suggested_edits },
    { label: 'pending resource proposal', n: counts.suggested_resources },
  ].filter((x) => x.n > 0);

  return (
    <div className="mt-5 rounded-lg border border-sage-200 bg-sand p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sage-700">
        <Merge size={13} aria-hidden="true" className="shrink-0" />
        What this merge will do
      </div>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-ink-soft">
          No linked records on the other listing. The merge will archive{' '}
          <span className="font-semibold">{loserName}</span> and point it at the
          winning record.
        </p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm text-ink">
          {items.map((it) => (
            <li key={it.label} className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-1.5 inline-block h-1 w-1 rounded-full bg-warm-400"
              />
              <span>
                Move{' '}
                <span className="font-semibold text-sage-800">{it.n}</span>{' '}
                {pluralize(it.label, it.n)} to the winning record
              </span>
            </li>
          ))}
          <li className="flex items-start gap-2">
            <span
              aria-hidden="true"
              className="mt-1.5 inline-block h-1 w-1 rounded-full bg-warm-400"
            />
            <span>
              Archive{' '}
              <span className="font-semibold">{loserName}</span> with a pointer
              to the winning record (no data lost)
            </span>
          </li>
        </ul>
      )}
    </div>
  );
}

function pluralize(word: string, n: number): string {
  if (n === 1) return word;
  // Handle multi-word phrases — pluralize the last token.
  if (word.endsWith('y') && !/[aeiou]y$/.test(word)) {
    return word.slice(0, -1) + 'ies';
  }
  return word + 's';
}

function deriveStatus(
  winnerValue: unknown,
  loserValue: unknown,
): 'agree' | 'auto_inherit_non_null' | 'conflict' {
  if (deepEqual(winnerValue, loserValue)) return 'agree';
  if (winnerValue === null || loserValue === null) return 'auto_inherit_non_null';
  return 'conflict';
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  return false;
}
